import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBell, faBellSlash, faMoon } from '@fortawesome/free-solid-svg-icons';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService, UserNotification } from '../../../services/notification.service';
import { DND_OPTIONS, DndMode, DoNotDisturbService } from '../../../services/do-not-disturb.service';

/**
 * Campana de notificaciones in-app: badge de no leídas, popover accionable y
 * toast temporal ante eventos en vivo (Karma recibido, retos 1v1).
 * Incluye el control de Modo Reunión / No Molestar (Snooze 30/60 min).
 */
@Component({
  selector: 'app-notifications-bell',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './notifications-bell.component.html',
  styleUrls: ['./notifications-bell.component.css'],
})
export class NotificationsBellComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationService);
  private readonly dnd = inject(DoNotDisturbService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  readonly faBell = faBell;
  readonly faMoon = faMoon;
  readonly faBellSlash = faBellSlash;
  readonly items$ = this.notifications.items$;
  readonly unreadCount$ = this.notifications.unreadCount$;
  readonly isDndActive$ = this.dnd.isActive$;
  readonly dndLabel$ = this.dnd.label$;
  readonly dndShortLabel$ = this.dnd.shortLabel$;
  readonly dndOptions = DND_OPTIONS;

  open = false;
  toast: UserNotification | null = null;

  ngOnInit(): void {
    this.notifications.incoming$.pipe(takeUntil(this.destroy$)).subscribe((n) => this.showToast(n));
    document.addEventListener('click', this.onDocumentClick);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.onDocumentClick);
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  private onDocumentClick = (): void => {
    this.open = false;
  };

  toggle(event: Event): void {
    event.stopPropagation();
    this.open = !this.open;
    if (this.open) {
      void this.notifications.loadFeed();
    }
  }

  /** Activa el Modo Reunión / No Molestar durante el periodo indicado. */
  snooze(mode: Exclude<DndMode, 'off'>, event: Event): void {
    event.stopPropagation();
    this.dnd.snooze(mode);
  }

  /** Reactiva las alertas de inmediato. */
  clearDnd(event: Event): void {
    event.stopPropagation();
    this.dnd.clear();
  }

  onItemClick(n: UserNotification): void {
    this.open = false;
    if (!n.readAt) {
      void this.notifications.markRead(n.id);
    }
    if (n.url) {
      void this.router.navigateByUrl(n.url);
    }
  }

  /** Abre la página con todo el historial y sus filtros. */
  openAll(): void {
    this.open = false;
    void this.router.navigateByUrl('/dashboard/notificaciones');
  }

  markAllRead(): void {
    void this.notifications.markAllRead();
  }

  private showToast(n: UserNotification): void {
    // Modo Reunión / No Molestar: no interrumpir con el toast (el evento queda en el feed).
    if (this.dnd.isActive()) {
      return;
    }
    // Los retos ya salen en su propio aviso con el botón de unirse.
    if (n.type === 'game_invite') {
      return;
    }
    this.toast = n;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.toast = null;
      this.toastTimer = null;
    }, 4000);
  }
}
